export const API_ENDPOINTS = {
  common: {
    userSettings: "/user-management/user/settings",
    uploadProfile: "/user-management/upload/profile",
    sideMenu: "/user-management/user/side-menu",
    companyConfigurationList: "/user-management/mt/company-configuration",
    meterAggregationQuantityNames: "/meter/aggregation/quantity-names",
    meterAggregationUomByQuantity: (quantityType: string): string =>
      `/meter/aggregation/uom/${quantityType}`,
    meterType: "/meter/dashboard/meter-types",
    sitesAll: "/meter/sites/all",
    siteDropdown: (companyId?: number | null): string =>
      `/meter/mt/site/dropdown?company_id=${companyId ?? ""}`,
    customerName: "/meter/metergroup/customer-names",
    systemConfiguration: "/meter/dashboard/configuration",
    updateNotificationStatus: "/reports/notification/status/update",
    meterGroups: "/meter/dashboard/meter-groups",
  },
  reports: {
    getAllTags: "/reports/get-all-tags",
    jurisdictions: "/reports/jurisdictions",
    dataValidation: {
      search: "/reports/data-validation/search",
      users: "/reports/data-validation/users",
      save: "/reports/data-validation",
      details: (id: number): string => `/reports/data-validation/${id}`,
      copy: (id: number): string =>
        `/reports/data-validation-report/copy/${id}`,
      execute: "/reports/execute-data-validation",
      updateStatus: "/reports/data-validation/update/status",
      updateCron: "/reports/data-validation/cron-expression",
      saveTags: "/reports/data-validation-report/save-tags",
      unmappedMeterChannels: (meterType: string): string =>
        `/reports/data-validation/unmapped-meter-channels/${meterType}`,
      meter: {
        mapped: (id: string | number): string =>
          `/reports/mt/data-validation/${id}/mapped/meter`,
        unmapped: (id: number, meterType: string): string =>
          `/reports/mt/data-validation/${id}/${meterType}/unmapped/meter`,
        save: (id: string | number): string =>
          `/reports/data-validation/${id}/saved/meter`,
        remove: (id: string | number): string =>
          `/reports/data-validation/${id}/removed/meter`,
      },
      meterGroup: {
        mapped: (id: string | number): string =>
          `/reports/mt/data-validation/${id}/mapped-meter-group`,
        unmapped: (id: string | number): string =>
          `/reports/mt/data-validation/${id}/unmapped-meter-groups`,
        save: (id: string | number): string =>
          `/reports/data-validation/${id}/saved/meter-group`,
        remove: (id: string | number): string =>
          `/reports/data-validation/${id}/removed/meter-group`,
      },
      notifications: {
        pending: "/reports/data-validation/notifications",
        updateStatus: "/reports/data-validation/notifications",
      },
    },
    enem12: {
      base: "/reports/schedule-report",
      search: "/reports/search/schedule-report",
      executionLogs: (reportType: string, logId: string | number): string =>
        `/reports/execution-logs/${reportType}/${logId}`,
      downloadOutput: "/reports/download/report-output/file",
      details: (id: string | number): string =>
        `/reports/schedule-report/${id}`,
      copy: (id: string | number): string =>
        `/reports/schedule-report/copy/${id}`,
      status: "/reports/schedule-report/status",
      updateCron: "/reports/schedule-report/update/cron-expression",
      saveTags: "/reports/schedule-report/save-tags",
      meterSerialProperty:
        "/reports/schedule-report/meter-serial-property-name",
      execute: "/reports/execute-enem12-report",
      output: {
        search: (reportId: number | string): string =>
          `/reports/schedule/report-output/search/${reportId}`,
        meterChannels: (reportId: number | string): string =>
          `/reports/schedule/report-output/meter-channels/${reportId}`,
        update: "/reports/schedule/report-output",
        add: (reportId: number | string): string =>
          `/reports/schedule/report-output/add/${reportId}`,
      },
      meter: {
        mapped: (id: string | number): string =>
          `/reports/mt/schedule/${id}/mapped/meter`,
        unmapped: (id: string | number): string =>
          `/reports/mt/schedule/${id}/unmapped/meter`,
        save: (id: string | number): string =>
          `/reports/schedule/${id}/saved/meter`,
        remove: (id: string | number): string =>
          `/reports/schedule/${id}/removed/meter`,
      },
      meterGroup: {
        mapped: (id: string | number): string =>
          `/reports/mt/schedule/${id}/mapped-meter-group`,
        unmapped: (id: string | number): string =>
          `/reports/mt/schedule/${id}/unmapped-meter-groups`,
        save: (id: string | number): string =>
          `/reports/schedule/${id}/saved/meter-group`,
        remove: (id: string | number): string =>
          `/reports/schedule/${id}/removed/meter-group`,
      },
    },
    mdn: {
      search: "/reports/mdn-report/search",
      status: "/reports/mdn-report/status",
      updateCron: "/reports/mdn-report/cron-expression",
      saveTags: "/reports/mdn-report/save-tags",
      execute: "/reports/execute-mdn-report",
      add: "/reports/mdn-report",
      details: (id: string | number): string => `/reports/mdn-report/${id}`,
      copy: (id: string | number): string => `/reports/mdn-report/copy/${id}`,
      mappedMeters: (id: string | number): string =>
        `/reports/mt/mdn-report/${id}/mapped/meter`,
      unmappedMeters: (id: string | number): string =>
        `/reports/mt/mdn-report/${id}/unmapped/meter`,
      saveMeters: (id: string | number): string =>
        `/reports/mdn-report/${id}/saved/meter`,
      removeMeters: (id: string | number): string =>
        `/reports/mdn-report/${id}/removed/meter`,
      mappedMeterGroups: (id: string | number): string =>
        `/reports/mt/mdn-report/${id}/mapped/meter-group`,
      unmappedMeterGroups: (id: string | number): string =>
        `/reports/mt/mdn-report/${id}/unmapped/meter-group`,
      saveMeterGroups: (id: string | number): string =>
        `/reports/mdn-report/${id}/saved/meter-group`,
      removeMeterGroups: (id: string | number): string =>
        `/reports/mdn-report/${id}/removed/meter-group`,
      output: {
        search: (reportId: number | string): string =>
          `/reports/mdn-report/report-output/search/${reportId}`,
        meterChannels: (reportId: number | string): string =>
          `/reports/mdn-report/report-output/meter-channels/${reportId}`,
        update: (reportId: number | string): string =>
          `/reports/mdn-report/report-output/${reportId}`,
      },
    },
    nem12QualityFlag: {
      search: "/reports/nem12-quality/search",
      status: "/reports/nem12-quality/status",
      saveTags: "/reports/nem12-quality-report/save-tags",
      execute: "/reports/execute-nem12-quality-flag-report",
      updateCron: "/reports/nem12-quality/cron-expression",
      details: (id: string | number): string => `/reports/nem12-quality/${id}`,
      add: "/reports/nem12-quality",
      copy: (id: string | number): string =>
        `/reports/nem12-quality-report/copy/${id}`,
      qualityFlags: "/meter/nem12/quality-flags",
      mappedMeters: (id: string | number): string =>
        `/reports/mt/nem12-quality-flag/${id}/mapped/meter`,
      unmappedMeters: (id: string | number): string =>
        `/reports/mt/nem12-quality-flag/${id}/unmapped/meter`,
      saveMappedMeters: (id: string | number): string =>
        `/reports/nem12-quality-flag/${id}/saved/meter`,
      removeMappedMeters: (id: string | number): string =>
        `/reports/nem12-quality-flag/${id}/removed/meter`,
      mappedMeterGroups: (id: string | number): string =>
        `/reports/mt/nem12-quality-flag/${id}/mapped/meter-group`,
      unmappedMeterGroups: (id: string | number): string =>
        `/reports/mt/nem12-quality-flag/${id}/unmapped/meter-group`,
      saveMappedMeterGroups: (id: string | number): string =>
        `/reports/nem12-quality-flag/${id}/saved/meter-group`,
      removeMappedMeterGroups: (id: string | number): string =>
        `/reports/nem12-quality-flag/${id}/removed/meter-group`,
    },
    meterEvent: {
      search: "/reports/meter-event-report/search",
      status: "/reports/meter-event-report/status",
      updateCron: "/reports/meter-event-report/cron-expression",
      saveTags: "/reports/meter-event-report/save-tags",
      execute: "/reports/execute-meter-event-report",
      add: "/reports/meter-event-report",
      details: (id: string | number): string =>
        `/reports/meter-event-report/${id}`,
      copy: (id: string | number): string =>
        `/reports/meter-event-report/copy/${id}`,
      mappedMeters: (id: string | number): string =>
        `/reports/mt/meter-event-report/${id}/mapped/meter`,
      unmappedMeters: (id: string | number): string =>
        `/reports/mt/meter-event-report/${id}/unmapped/meter`,
      saveMappedMeters: (id: string | number): string =>
        `/reports/meter-event-report/${id}/saved/meter`,
      removeMappedMeters: (id: string | number): string =>
        `/reports/meter-event-report/${id}/removed/meter`,
      mappedMeterGroups: (id: string | number): string =>
        `/reports/mt/meter-event-report/${id}/mapped/meter-group`,
      unmappedMeterGroups: (id: string | number): string =>
        `/reports/mt/meter-event-report/${id}/unmapped/meter-group`,
      saveMappedMeterGroups: (id: string | number): string =>
        `/reports/meter-event-report/${id}/saved/meter-group`,
      removeMappedMeterGroups: (id: string | number): string =>
        `/reports/meter-event-report/${id}/removed/meter-group`,
    },
    nem12: {
      search: "/reports/nem12-report/search",
      execute: "/reports/execute-nem12-report",
      saveTags: "/reports/nem12-report/save-tags",
      updateCron: "/reports/nem12-report/cron-expression",
      updateStatus: "/reports/nem12-report/status",
      resendDetail: "/reports/nem12-report/report-resend-detail",
      base: "/reports/nem12-report",
      reportData: "/reports/nem12/data",
      statusData: "/meter/nem12/status/data",
      transactionCodes: "/reports/nem12/transaction-code",
      details: (id: string | number): string => `/reports/nem12-report/${id}`,
      copy: (id: string | number): string => `/reports/nem12-report/copy/${id}`,
      output: {
        search: (reportId: number | string): string =>
          `/reports/nem12-report/report-output/search/${reportId}`,
        meterChannels: (reportId: number | string): string =>
          `/reports/nem12-report/report-output/meter-channels/${reportId}`,
        update: (reportId: number | string): string =>
          `/reports/nem12-report/report-output/${reportId}`,
        add: (reportId: number | string): string =>
          `/reports/nem12-report/report-output/add/${reportId}`,
      },

      meter: {
        mapped: (id: string | number): string =>
          `/reports/mt/nem12-report/${id}/mapped/meter`,
        unmapped: (id: string | number): string =>
          `/reports/mt/nem12-report/${id}/unmapped/meter`,
        remove: (reportId: number | string): string =>
          `/reports/nem12-report/${reportId}/removed/meter`,
        save: (reportId: number | string): string =>
          `/reports/nem12-report/${reportId}/saved/meter`,
      },
      meterGroup: {
        mapped: (id: string | number): string =>
          `/reports/mt/nem12-report/${id}/mapped/meter-group`,
        unmapped: (id: string | number): string =>
          `/reports/mt/nem12-report/${id}/unmapped/meter-group`,
        remove: (reportId: number | string): string =>
          `/reports/nem12-report/${reportId}/removed/meter-group`,
        save: (reportId: number | string): string =>
          `/reports/nem12-report/${reportId}/saved/meter-group`,
      },
      statusMapping: (reportId: number | string): string =>
        `/reports/nem12-report/status/mapping/${reportId}`,
      deleteStatusMapping: (reportId: number | string): string =>
        `/reports/nem12-report/status/mapping/${reportId}`,
    },
    orionCumulative: {
      add: "/report-processor/orion-report/add",
      search: "/report-processor/orion-report",
      execute: "/report-processor/execute-orion-report",
      status: "/report-processor/orion-report/status",
      updateCron: "/report-processor/orion-report/cron-expression",
      saveTags: "/report-processor/orion-reports/tags",
      mappedMeters: "/report-processor/mt/orion-report/meters/mapped",
      mappedMeterGroups:
        "/report-processor/mt/orion-report/meter-groups/mapped",
      removeMappedMeterGroups:
        "/report-processor/orion-reports/meter-groups/remove",
      removeMappedMeters: "/report-processor/orion-report/meters/remove",
      details: (id: string | number): string =>
        `/report-processor/orion-report/${id}`,
      copy: (id: string | number): string =>
        `/report-processor/orion-report/copy/${id}`,
      channelDetail: (id: string | number): string =>
        `/report-processor/orion-report/channel-detail/${id}`,
      channelList: (id: string | number): string =>
        `/report-processor/orion-report/channel-list/${id}`,
      channelDetailUpdate:
        "/report-processor/orion-report/channel-detail/update",
      unmappedMeters: "/report-processor/mt/orion-report/meters/unmapped",
      addMappedMeters: "/report-processor/orion-report/meters/add",
      unmappedMeterGroups:
        "/report-processor/mt/orion-report/meter-groups/unmapped",
      addMappedMeterGroups: "/report-processor/orion-report/meter-groups/add",
    },
    siteUsage: {
      search: "/report-processor/site-usage-report",
      add: "/report-processor/site-usage-report/add",
      details: (id: string | number): string =>
        `/report-processor/site-usage-report/${id}`,
      copy: (id: string | number): string =>
        `/report-processor/site-usage-report/copy/${id}`,
      status: "/report-processor/site-usage-report/status",
      updateCron: "/report-processor/site-usage-report/cron-expression",
      saveTags: "/report-processor/site-usage-report/tags",
      execute: "/report-processor/execute-site-usage-report",
      sites: "/report-processor/site-usage-report/sites",
    },
    dataStatus: {
      meterSites: "/reports/data-status/meter-sites",
      meterProperties: "/reports/data-status/meter-properties",
      meterIds: "/reports/mt/data-status/meter-ids",
      meterChannels: (id: string): string => `/meter/meterchannel/${id}`,
      preview: "/reports/meter-data/data-status-preview",
    },
    advancedEcoValidation: {
      jobDetail: "/advance-eco-data-processor/summary/job-detail",
      customer: "/advance-eco-data-processor/summary/customer",
      site: "/advance-eco-data-processor/summary/site",
      meter: "/advance-eco-data-processor/summary/meter",
    },
    notification: {
      failed: "/reports/system/notification/failed",
      search: "/reports/notification-log/search",
      updateStatus: "/reports/notification/status/update",
    },
    anomaly: {
      algorithmDetails: "/anomaly-management/anomaly/algorithm-details",
      dashboard: "/anomaly-management/anomaly/dashboard",
      dashboardCounts: "/anomaly-management/anomaly/dashboard/counts",
      meterDetails: "/anomaly-management/anomaly/meter-details",
      readingDetails: "/anomaly-management/anomaly/reading-details",
    },
  },
  dashboard: {
    meterSummary: (id: number | string): string =>
      `/meter/meterreading/metersummarycount/${id}`,
    siteSummary: "/meter/meterreading/sitesummarycount",
    widgets: {
      lowBatteryMeterCount: "/meters/dashboard/widget/low-battery-meter-count",
      lowBatteryMeter: "/meters/dashboard/widget/low-battery-meter",
      failedReportCount: (timezone: string): string =>
        `/meters/dashboard/widget/failed-report-count?timezone=${encodeURIComponent(timezone)}`,
      validationSummaryRuleCount: (timezone: string): string =>
        `/meters/dashboard/widget/validation-summary-rule-count?timezone=${encodeURIComponent(timezone)}`,
      offlineMeterCount: "/meters/dashboard/widget/offline-meter-count",
      offlineMeter: "/meters/dashboard/widget/offline-meter",
      dataSourcesStats: (timezone: string): string =>
        `/meters/dashboard/widget/data-sources-stats?timezone=${encodeURIComponent(timezone)}`,
    },
    siteDetail: (siteId: number | string): string => `/meter/site/${siteId}`,
    siteAllProperties: (siteId: number | string): string =>
      `/meter/siteallproperties/${siteId}`,
    scheduleLinks: (siteId: number | string): string =>
      `/meter/schedule-links/search/${siteId}`,
    meterDetailsByType: (id: number, meterType: string): string =>
      `/meter/mt/meterdetailsbytype/${id}/${meterType}`,
    getFinalMeters: (siteId: number | string): string =>
      `/meter/reading/final-meters/${siteId}`,
    finalReading: "/meter/finalreading",
    channelList: "/reports/mt/meter-data/channel-list",
    quickSearchMeters: "/meter/mt/dashboard/quick-search-meters",
    meterDetail: (meterId: string): string =>
      `/meter/dashboard/meterdetail/${meterId}`,
    uoms: (meterType: string): string => `/meter/dashboard/uoms/${meterType}`,
    tariffDetails: (id: number | string): string =>
      `/meter/dashboard/tariff-details/${id}`,
    touSummary: "/meter/dashboard/tou",
    meterData: "/meter/dashboard/reading/search",
  },
  management: {
    meter: {
      base: "/meter/mt/meter",
      search: "/meter/mt/meter/search",
      details: (id: number | string): string => `/meter/mt/meter/${id}`,
      calculateOffsetDifference: "/meter/calculate/offset/difference",
      updateLifeSupport: "/meter/mt/update/lifesupport",
      updateStatus: "/meter/mt/update/status",
      addCustomProperty: "/meter/mt/meter/add/custom-property",
      selectedMeterChannelList: "/meter/selected-meter-channel-list",
      missingMeterChannelList: "/meter-data/mt/get-cumulative-channel-list",
      missingTimestampCount: "/meter-data/missing-timestamp-count",
      addMissingMeterReadings: "/meter/mt/add-missing-reading",
      deleteMeterReadings: "/meter/mt/delete-selected-meter-data",
      deleteIotRawData: "/meter-data/iot/raw-data",
      meterEvents: "/meter/meter-events",
      meterAssociation: "/meter/meter-association",
      currentStatus: (encryptedMeterId: string): string =>
        `/meter/current/status/${encryptedMeterId}`,
      lastReading: "/meter/view/last/reading",
      cetRealTimeData: "/cet-processor/cetmeter/getrealtimedata",
      cetCheckPassword: "/cet-processor/cetmeter/checkpassword",
      cetControl: "/cet-processor/cetmeter/control",
      meterDetail: (encryptedMeterId: string): string =>
        `/meter/dashboard/meterdetail/${encryptedMeterId}`,
      meterChannels: (encryptedMeterId: string): string =>
        `/meter/meterchannel/${encryptedMeterId}`,
      channelDetail: (channelId: number | string): string =>
        `/meter/channel/${channelId}`,
      meterReadingSearch: "/meter/meterreading/search",
      uomsAll: "/meter/virtual-meter/uoms/all",
      addChannel: "/meter/channel/add",
      updateChannel: (channelId: number | string): string =>
        `/meter/channel/update/${channelId}`,
      nem12StatusData: "/meter/nem12/status/data",
      addMeterReadingData: "/meter/meter-reading/add",
      updateMeterReadingData: "/meter/meter-reading",
      deleteMeterReadingData: "/meter/delete/meter-reading",
    },
    virtualMeter: {
      base: "/meter/virtual-meter",
      search: "/meter/search/virtual-meter",
      copy: (id: number | string): string => `/meter/virtual-meter/copy/${id}`,
      delete: "/meter/virtual-meter",
      details: (id: number | string): string => `/meter/virtual-meter/${id}`,
      channels: (id: number | string): string =>
        `/meter/dashboard/virtual-meters/channel/${id}`,
      readings: "/meter/virtual-meters/readings",
      mappedMeters: (id: number | string): string =>
        `/meter/virtual-meter/mapped-meter/${id}`,
      unmappedMeters: (id: number | string): string =>
        `/meter/virtual-meter/meter/${id}`,
      saveMeters: (id: number | string): string =>
        `/meter/virtual-meter/add-meter/${id}`,
      removeMeters: (id: number | string): string =>
        `/meter/virtual-meter/meter/${id}`,
      mappedMeterGroups: (id: number | string): string =>
        `/meter/virtual-meter/${id}/mapped-meter-group`,
      unmappedMeterGroups: (id: number | string): string =>
        `/meter/virtual-meter/${id}/unmapped-meter-group`,
      saveMeterGroups: (id: number | string): string =>
        `/meter/virtual-meter/${id}/saved/meter-group`,
      removeMeterGroups: (id: number | string): string =>
        `/meter/virtual-meter/${id}/removed/meter-group`,
      calculationChannels: (channelType: string, id: number | string): string =>
        `/meter/virtual-meter/data-calculation/channels/${channelType}/${id}`,
      dataCalculation: (id: number | string): string =>
        `/meter/virtual-meter/data-calculation/${id}`,
      saveDataCalculation: (id: number | string): string =>
        `/meter/virtual-meter/${id}/data-calculation`,
    },
    meterGroup: {
      base: "/meter/mt/metergroup",
      search: "/meter/mt/metergroup/search",
      userSearch: "/user-management/user/search",
      assignUsers: "/meter/user-meter-groups/assign",
      mappedVirtualMeters: (meterGroupId: number): string =>
        `/meter/metergroup/mapped-virtual-meter/${meterGroupId}`,
      virtualMeterBase: "/meter/metergroup/virtual-meter",
      saveVirtualMetersToMeterGroup: (meterGroupId: number): string =>
        `/meter/metergroup/add-virtual-meter/${meterGroupId}`,
      mappedMeters: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/meter/mapped/${meterGroupId}`,
      mappedUsers: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/user/mapped/${meterGroupId}`,
      userBase: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/user/${meterGroupId}`,
      addUsers: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/add-user/${meterGroupId}`,
      meterBase: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/meter/${meterGroupId}`,
      addMeters: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/add-meter/${meterGroupId}`,
      removeMeters: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/remove-meter/${meterGroupId}`,
      status: (meterGroupId: number | string): string =>
        `/meter/mt/metergroup/${meterGroupId}/status`,
    },
    site: {
      base: "/meter/mt/site",
      search: "/meter/mt/site/search",
      contactBase: "/meter/mt/site/contact",
      addContact: (siteId: number | string): string =>
        `/meter/mt/site/add/contact/${siteId}`,
      mappedContact: (siteId: number | string): string =>
        `/meter/mt/site/contact/mapped/${siteId}`,
      removeContact: (siteId: number | string): string =>
        `/meter/mt/site/remove-contact/${siteId}`,
      unmappedMeter: (siteId: number | string): string =>
        `/meter/mt/site/meter/unmapped/${siteId}`,
      mappedMeter: (siteId: number | string): string =>
        `/meter/mt/site/meter/mapped/${siteId}`,
      addMeters: (siteId: number | string): string =>
        `/meter/mt/site/meter/${siteId}`,
      meterBase: "/meter/site/meter",
      mappedMeterGroups: (siteId: number | string): string =>
        `/meter/mt/site/meter-groups/${siteId}`,
      mappedCompanies: (siteId: number | string): string =>
        `/meter/mt/site/associated-companies/${siteId}`,
      status: "/meter/mt/site/update/status",
      scheduleLinkBase: "/meter/schedule-links",
      scheduleLinkSearch: (siteId: number | string): string =>
        `/meter/schedule-links/search/${siteId}`,
    },
    meterHierarchy: {
      siteMeterTypes: (siteId: number | string): string =>
        `/meters/meter-hierarchy/site-meter-types/${siteId}`,
      import: "/meters/meter-hierarchy/import",
      publish: "/meters/meter-hierarchy/publish",
      tagsList: "/meters/meter-hierarchy/tags/list",
      tagsBase: "/meters/meter-hierarchy/tags",
      tagsReorder: "/meters/meter-hierarchy/reorder/tags",
      tagById: (tagId: number | string): string =>
        `/meters/meter-hierarchy/tags/${tagId}`,
      tagMeters: "/meters/meter-hierarchy/tags/meters",
      hierarchy: "/meters/meter-hierarchy/hierarchy",
      hierarchyManage: "/meters/meter-hierarchy/hierarchy/manage",
      hierarchyNodes: (nodeId: number | string): string =>
        `/meters/meter-hierarchy/hierarchy/nodes/${nodeId}`,
      meters: "/meters/meter-hierarchy/meters",
      rollupRole: "/meters/meter-hierarchy/rollup-role",
      tagAssignmentAssign: "/meters/meter-hierarchy/tag-assignments/assign",
      tagAssignmentUnassign: "/meters/meter-hierarchy/tag-assignments/unassign",
      tagAssignmentAssignList:
        "/meters/meter-hierarchy/tag-assignments/assign-list",
      tagAssignmentUnassignList:
        "/meters/meter-hierarchy/tag-assignments/unassign-list",
      rootNodes: "/meters/meter-hierarchy/root-nodes",
    },
    user: {
      base: "/user-management/mt/user",
      search: "/user-management/mt/user/search",
      toggleActive: "/user-management/user/active",
      resetMfaSession: "/user-management/reset-mfa-session",
      siteList: "/user-management/mt/site-list",
      markAsSiteContact: "/user-management/mt/user/mark-as-site-contact",
      delete: (userId: number): string => `/user-management/mt/user/${userId}`,
      mappedMeterGroups: "/user-management/mt/user/search-mapped-metergroups",
      unmappedMeterGroups: "/user-management/mt/user/metergroups/search",
      meterGroups: "/user-management/mt/user/metergroups",
      removeMeterGroups: "/user-management/mt/user/remove-metergroups",
      meterSchedule: {
        mapped: "/meter/logic-app/meter-schedule",
        remove: "/meter/logic-app/meter-schedule",
        save: "/meter/logic-app/save/metertypes/site",
        unmapped: "/meter/mt/logic-app/unassigned/metertypes/site",
      },
      getRolesByCompanyId: (companyId: number | string): string =>
        `/user-management/company-configuration/all-roles/${companyId}`,
      getAllowedUsers: (companyId: number | string): string =>
        `/user-management/allow-user/${companyId}`,
    },
    contact: {
      search: "/meter/mt/contact/search",
      companyConfigs: "/user-management/company-configuration",
      create: "/meter/mt/contact",
      byId: (id: number | string): string => `/meter/mt/contact/${id}`,
      searchAssignedSites: (id: number | string): string =>
        `/meter/mt/contact/mapped-site/${id}`,
      searchAvailableSites: (contactId: number | string): string =>
        `/meter/mt/contact/unmapped-site/${contactId}`,
      saveSite: (contactId: number | string): string =>
        `/meter/mt/contact/save-site/${contactId}`,
      removeSite: (contactId: number | string): string =>
        `/meter/mt/contact/remove-site/${contactId}`,
    },
    companySettings: {
      base: "/user-management/mt/company-configuration",
      search: "/user-management/mt/company-configuration/search",
      roles: "/user-management/company-configuration/roles",
      mappedSites: "/user-management/mt/company-configuration/mapped/sites",
      mappedMeterGroups:
        "/user-management/mt/company-configuration/mapped/meter-groups",
      uploadLogo: "/user-management/mt/company-configuration/logo/upload",
      deleteLogo: "/user-management/mt/company-configuration/logo/remove",
      status: (companyId: number | string): string =>
        `/user-management/mt/company-configuration/${companyId}/status`,
      changeLogs: (companyId: number | string): string =>
        `/user-management/company-configuration/${companyId}/change-logs`,
    },
    role: {
      base: "/user-management/role",
      search: "/user-management/role/search",
      users: "/user-management/role/users",
      searchUsers: "/user-management/role/search-users",
      saveUsers: "/user-management/role/save-users",
      removeUsers: "/user-management/role/remove-users",
      modulePageRights: (roleId: number): string =>
        `/user-management/role/module-page-rights/${roleId}`,
      updateModulePageRights: "/user-management/role/module-page-rights",
    },
  },
  reportHistory: {
    logs: (id: string | number): string => `/reports/logs/${id}`,
    substitutedValueDetail: "/reports/substitutedvalue-detail",
  },
  validationHistory: {
    getLogsList: "/reports/data-validation/report-output",
    downloadBlob: "/reports/data-validation/report/download",
    getRuleOutput: (id: number): string =>
      `/reports/data-validation/rule-output/${id}`,
    getRuleOutputData: (id: number): string =>
      `/reports/data-validation/rule-output-data/${id}`,
    rulesSummary: "/reports/data-validation/rules-summary",
    ruleByMeterSummary: "/reports/data-validation/rule-by-meter-summary",
  },
  dataTools: {
    dataCorrections: {
      getLogsList: "/meter/nem12-report/get-report-correction-data",
      getMeterCorrectionData: "/meter/nem12-report/get-meter-correction-data",
      markAsFinalAndResend: "/meter/nem12-report/mark-as-final-and-resend",
      getReadingCorrectionData:
        "/meter/nem12-report/get-reading-correction-data",
      saveReportCorrectionData:
        "/meter/nem12-report/save-report-correction-data",
    },
    serial: {
      list: "/meter/get-serial-numbers-list",
      generate: "/meter/serial-number-generate",
      update: "/meter/updateSerials",
    },
    property: {
      list: "/meter/get-property-numbers-list",
      generate: "/meter/property-number-generate",
      update: "/meter/updateProperty",
    },
    nmi: {
      list: "/meter/get-nmis-list",
      generate: "/meter/nmi-generate",
      update: "/meter/updateNMI",
    },
    nem12Viewer: {
      base: `${import.meta.env.VITE_NEM12_API_URL}/v1/nem12-batches`,
      duplicates: (batchId: string): string =>
        `${import.meta.env.VITE_NEM12_API_URL}/v1/nem12-batches/${batchId}/duplicates`,
    },
  },
  alarmsSetup: {
    alarmEvents: {
      search: "/meter-data/search/meter-event-alarm",
      base: "/meter-data/meter-event-alarm",
      details: (id: number): string => `/meter-data/meter-event-alarm/${id}`,
      copy: (id: number): string => `/meter-data/meter-event-alarm/copy/${id}`,
      iotPayloadProperty: "/meter-data/iot-payload-property",
      meterEvents: "/meter-data/meter-events/all",
      propertiesAll: "/meter-data/properties/all",
      mappedMeters: (id: number): string =>
        `/meter-data/mt/meter-event-alarm/${id}/mapped/meter`,
      unmappedMeters: (id: number): string =>
        `/meter-data/mt/meter-event-alarm/${id}/unmapped/meter`,
      savedMeters: (id: number): string =>
        `/meter-data/meter-event-alarm/${id}/saved/meter`,
      removedMeters: (id: number): string =>
        `/meter-data/meter-event-alarm/${id}/removed/meter`,
      mappedMeterGroups: (id: number): string =>
        `/meter-data/mt/meter-event-alarm/${id}/mapped/meter-group`,
      unmappedMeterGroups: (id: number): string =>
        `/meter-data/mt/meter-event-alarm/${id}/unmapped/meter-group`,
      savedMeterGroups: (id: number): string =>
        `/meter-data/meter-event-alarm/${id}/saved/meter-group`,
      removedMeterGroups: (id: number): string =>
        `/meter-data/meter-event-alarm/${id}/removed/meter-group`,
    },
    alarmProperty: {
      base: "/meter-data/iot-payload-property",
    },
  },
  integrations: {
    iotSync: {
      list: "/meter-data/iot/dev-eui/list",
      sync: "/iot-data-processor/iot/data/sync",
    },
    unprovisionedIotDevices: {
      list: "/meter-data/iot/dev-eui/list",
      resync: "/iot-data-processor/iot/unprovisioned-data",
    },
    cetSync: {
      list: "/cet-processor/cetmeter/get-cet-meters",
      sync: "/meter-data/cetmeter/data/sync",
      validateSerials: "/cet-processor/cetmeter/validate-serials",
    },
    advancedEcoSync: {
      list: "/advance-eco-data-processor/meter-data/get-ae-meters",
      sync: "/advance-eco-data-processor/meter-data/import",
    },
  },
  platformConfig: {
    existingChannels: `/meter/configuration/meter-channels-configuration`,
    channelsOptions: `/meter/configuration/meter-channels`,
    saveChannels: "/meter/configuration/save/meter-channel",
    uomConfig: {
      get: "/meter/configuration/uom-config",
      save: "/meter/configuration/uom-config",
    },
    dashboardMeterChannelConfiguration: {
      get: "/meter/configuration/dashboard-meter-channel-configuration",
      save: "/meter/configuration/save-dashboard-meter-channel-configuration",
      energyForms: "/meter/configuration/energy-forms",
      energyDirections: "/meter/configuration/energy-directions",
      energyFormUom: (energyForm: string): string =>
        `/meter/configuration/energy-forms/uom?energy_form=${encodeURIComponent(energyForm)}`,
      delete: (configurationId: number): string =>
        `/meter/configuration/dashboard-meter-channel-configuration/${configurationId}`,
    },
    widgetConfiguration: {
      get: "/meters/dashboard/widget/configuration",
      save: "/meters/dashboard/widget/configuration",
    },
    enem12: {
      nmiConfiguration: "/meter/configuration/nmi-configuration",
      nmiSuffix: "/meter/configuration/nmi-suffix",
    },
    systemConfiguration: {
      get: "/meter/configuration/system",
      save: "/meter/configuration/save/system-configuration",
      nem12StatusData: "/meter/nem12/status/data",
      users: "/reports/data-validation/users",
      uploadDefaultLogo: "/user-management/upload/default/logo",
    },
    emailSettings: {
      base: "/meter/configuration/email",
      test: "/meter/configuration/email/test",
    },
    tags: {
      getAllTags: "/reports/get-all-tags",
      getTagConfiguration: "/reports/configuration/get-tag",
      save: "/reports/configuration/add-tag",
      update: "/reports/configuration/edit-tag",
      delete: "/reports/configuration/delete-tag",
    },
    reportEmailBody: {
      get: (reportType: string): string =>
        `/reports/configuration/body-configuration/${encodeURIComponent(reportType)}`,
      update: "/reports/configuration/update/body-configuration",
    },
    cetCustomerPassword: {
      customerList: "/meter/configuration/cet-password/customer-list",
      customer: "/meter/configuration/cet-password/customer",
    },
    nem12: {
      streamIdentifier: {
        search: "/reports/mdm-data-stream-identifier/search",
        add: "/reports/mdm-data-stream-identifier",
      },
      dataSubstitutionType: {
        search: "/reports/data-substitution-type/search",
        add: "/reports/data-substitution-type",
      },
      registerId: {
        search: "/reports/register-id/search",
        add: "/reports/register-id",
      },
      transactionCode: {
        search: "/reports/transaction-code/search",
        add: "/reports/transaction-code",
      },
      statusMapping: "/reports/nem12-status-mapping",
      nem12NmiSuffix: "/meter/configuration/nem12-nmi-suffix",
      nem12NmiConfigChannels: "/meter/configuration/nmi-configuration/channels",
    },
    dataValidationReportChannel: {
      get: "/reports/configuration/validation-channels",
      save: "/reports/configuration/validation-channels",
    },
    customProperty: {
      get: "/meter/configuration/custom-properties",
      save: "/meter/configuration/custom-properties",
    },
    meterSchedule: {
      availableColumns: "/meter/meter-schdedule/columns",
      storageColumns: "/meter/azure/storage/columns",
      saveStorageColumns: "/meter/azure/storage/columns",
      uploadColumns: "/meter/import/meter-schdedule/columns",
    },
    deviceStatus: {
      get: "/meter/configuration/device/status",
      save: "/meter/configuration/device/status",
    },
    tou: {
      list: "/meter/configuration/tou",
      create: "/meter/configuration/tou",
      update: (id: number): string => `/meter/configuration/tou/${id}`,
      delete: (id: number): string => `/meter/configuration/tou/${id}`,
      tariff: {
        get: (touId: number): string =>
          `/meter/configuration/tou-tariff/${touId}`,
        save: (touId: number): string =>
          `/meter/configuration/tou-tariff/${touId}`,
      },
    },
    holidayList: {
      list: "/meter/configuration/get-holiday-list",
      upload: "/meter/configuration/save-holiday-list",
      delete: "/meter/configuration/delete-holidays",
    },
  },
  system: {
    contactSupport: {
      search: "/user-management/contact-support/search",
      submit: "/user-management/contact-support",
      details: (id: number | string): string =>
        `/user-management/contact-support/${id}`,
      companies: "/user-management/company-configuration",
      roles: "/user-management/user/roles",
      requester: (id: number | string): string => `/user-management/user/${id}`,
      uploadDocuments: "/user-management/upload/documents",
      companyConfiguration: (companyId: number | string): string =>
        `/user-management/company-configuration/${companyId}`,
    },
    meterStatusCodes: {
      base: "/meter/status",
      search: "/meter/status/search",
    },
  },
  monitoring: {
    deviceStatus: {
      list: "/meter/mt/device-status",
    },
    meterDataAudit: {
      search: "/meter/audittrial",
    },
    meterSchedule: {
      assigned: "/meter/meter-schedule/site-wise/assigned",
      siteSearch: "/meter/meter-schedule/site/search",
      meterTypes: "/meter/meter-schedule/meter-types",
      searchParameters: "/meter/meter-schedule/search/parameters",
    },
  },
  logs: {
    // User Audit Logs Module
    userAudit: {
      search: "/user-management/get-sign-in-logs",
    },
    // Cleanup Module
    cleanup: {
      base: "/meter/configuration/logs/cleanup",
    },
    // Report Retention Module
    reportRetention: {
      base: "/meter/configuration/logs/report-retention",
    },
    // System Logs Module (Activity-based logs)
    system: {
      search: "/meter-data/logs/import/schedule",
      reprocess: "/meter-data/reprocessed/blobs",
      download: "/meter-data/download/error/blobs",
      delete: "/meter-data/delete/error/blobs",
    },
  },
  metrixInsights: {
    login: "/user-management/superset-login",
    refreshToken: "/user-management/superset-refresh-token",
    identityToken: "/user-management/superset-identity-token",
    embeddedDashboard: (id: number): string =>
      `/user-management/get-embedded-dashboard/${id}`,
    embeddedDashboardList: "/user-management/superset-embedded-dashboard-list",
    dashboardList: "/user-management/superset-dashboard-list",
    saveEmbeddedDashboard: "/user-management/save-superset-embedded-dashboard",
  },
  provisioning: {
    meterScheduleImport: "/meter-data/mt/import",
    advancedEcoDevices: "/advance-eco-data-processor/meter-import/devices",
    advancedEcoImport:
      "/advance-eco-data-processor/mt/meter-import/import-details",
    unprovisionedMeters: {
      search: "/meter/unprovisionedmeter",
      delete: "/meter/remove/unprovisionedmeter",
    },
    chirpstackDeviceUpload: "/meter-data/chirpstack/device-file-upload",
    chirpstackGatewayUpload: "/meter-data/chirpstack/gateway-file-upload",
  },
  iotDataProcessor: {
    dataSummary: "/iot-data-processor/iot/data-summary",
    dataRaw: "/iot-data-processor/iot/data-raw",
  },
};
